import axiosInstance from './axiosInstance';
import axios from 'axios';

/**
 * Get presigned URL for S3 upload
 * @param {string} folder - S3 folder path (e.g., 'posters', 'images')
 * @param {File} file - File object to upload
 * @returns {Promise<{uploadUrl: string, fileKey: string, publicUrl: string}>}
 */
export const getPresignedUrl = async (folder, file) => {
  const response = await axiosInstance.post('/admin/upload/presigned-url', {
    folder,
    filename: file.name,
    mimetype: file.type,
  });
  return response.data.data;
};

/**
 * Upload file directly to S3 using presigned URL
 * @param {string} presignedUrl - Presigned URL from backend
 * @param {File} file - File object to upload
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<void>}
 */
export const uploadToS3 = async (presignedUrl, file, onProgress) => {
  await axios.put(presignedUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });
};

/**
 * Complete upload process: get presigned URL and upload to S3
 * @param {string} folder - S3 folder path
 * @param {File} file - File object to upload
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{fileKey: string, publicUrl: string}>}
 */
export const uploadFile = async (folder, file, onProgress) => {
  // Step 1: Get presigned URL
  const { uploadUrl, fileKey, publicUrl } = await getPresignedUrl(folder, file);

  // Step 2: Upload to S3
  await uploadToS3(uploadUrl, file, onProgress);

  // Step 3: Return file info
  return { fileKey, publicUrl };
};

const uploadService = {
  getPresignedUrl,
  uploadToS3,
  uploadFile,
};

export default uploadService;
